import {
  ReadLandingPageDomain,
  UpdateLandingPageDomain,
} from '@tinct/core-api-models';
import {ApiClient} from '../api-client/api-client';

/**
 * The workspace's single custom hosting domain for landing pages.
 * Singleton resource: one domain per organization, no id in the path.
 * Pages are always live on their tinct domain regardless; this only adds
 * `{baseUrl}{page-slug}/` URLs for every published page of the workspace.
 */
export class LandingPageDomainEndpoint {
  constructor(private client: ApiClient) {}

  /** Always 200 — fields are null while no domain is registered. */
  async getLandingPageDomain(
    organizationId: string,
  ): Promise<ReadLandingPageDomain> {
    return this.client.getById<ReadLandingPageDomain>(
      `/api/v1/organizations/${organizationId}/landing-page-domain`,
    );
  }

  /**
   * Register or replace the domain. Starts provisioning (`pending_dns`) and
   * backfills already-published pages. Re-PUT of the same domain after
   * `failed` restarts provisioning (retry).
   */
  async updateLandingPageDomain(
    organizationId: string,
    data: UpdateLandingPageDomain,
  ): Promise<ReadLandingPageDomain> {
    return this.client.put<ReadLandingPageDomain>(
      `/api/v1/organizations/${organizationId}/landing-page-domain`,
      data,
    );
  }

  /** Advance provisioning now instead of waiting for the server poller. */
  async checkLandingPageDomain(
    organizationId: string,
  ): Promise<ReadLandingPageDomain> {
    return this.client.post<ReadLandingPageDomain>(
      `/api/v1/organizations/${organizationId}/landing-page-domain/check`,
    );
  }

  /**
   * Remove the domain and its path-based page copies. Tinct-domain URLs are
   * untouched — pages stay live there.
   */
  async deleteLandingPageDomain(organizationId: string): Promise<void> {
    return this.client.delete<void>(
      `/api/v1/organizations/${organizationId}/landing-page-domain`,
    );
  }
}
